import {
  controlsActivation,
  controlsCombatant,
  mayPlayMove,
  planningTurnState,
  previewActivation,
  type Combatant,
  type EncounterCommand,
  type EncounterState,
} from "@lancer-vtt/domain";

/**
 * Who is asking: the viewer's own slot id (the identity the room attributes a command to) and
 * whether that slot is the GM's. The GM is the pre-activation controller of every unit
 * ({@link controlsActivation}), so the preview needs the role as well as the slot.
 */
export type FoldViewer = { actorId: string | null | undefined; isGm: boolean };

/**
 * The **client-side mirror of the room's fold arm** — the ONE expression every pre-activation
 * affordance in the browser composes an existing, untouched legality function against
 * (`projects/pre-activation-affordances.md` D4).
 *
 * Returns {@link previewActivation}'s hypothetical post-activation state when `actorId` would
 * trigger the room's fold on `combatant`, and `state` itself otherwise. That is
 * `activationDisposition`'s **first arm and only its first arm**: `perUnitState === "ready"`
 * (answered inside `previewActivation`, which returns `state` unchanged for anything that is not
 * ready — wrong side, already acted, destroyed, mid-turn, or not an actor at all) ∧
 * {@link controlsActivation} (the half `previewActivation` cannot know, because it takes no caller).
 *
 * **`controlsActivation`, never the frontend's `canControl`.** `CombatApp.tsx` defines the
 * `canControl` prop as `session.role === "gm" || controlsCombatant(c, actorId)`; the room's fold
 * arm is `controlsActivation`: strict slot identity, plus the GM on every unit (2026-09-28). The two
 * agree for the GM, but not for a player holding no slot on the unit, and the room's arm is the one
 * a commit meets.
 *
 * **The returned state is READ-ONLY** — never committed, never dispatched, never handed to a
 * reducer as the state to apply against. See {@link previewActivation}'s never-commit invariant;
 * this function inherits it wholesale.
 *
 * Consumers: {@link inspectorEconomy} (the ability bar + the budget-gated choosers, 2.2/2.3), the
 * movement-range wash and drag-path preview (`useTargetingWashes`, 2.4), `CombatApp`'s
 * `canPlayMove` drag affordance (2.4), and `RemoteEncounterController.dispatchMove`'s local
 * legality dry-run (2.4 — the one place in Phase 2 where D7 genuinely bites, because that dry-run
 * returns `err` **without sending**).
 */
export function previewIfControlled(
  state: EncounterState,
  combatant: Pick<Combatant, "id" | "controllerSlotId" | "coControllerSlotIds"> | undefined,
  viewer: FoldViewer,
): EncounterState {
  if (combatant === undefined) return state;
  return controlsActivation(combatant, viewer.actorId, viewer.isGm) ? previewActivation(state, combatant.id) : state;
}

/**
 * **The state a move affordance reads** (`projects/off-turn-planning.md` 2.1): the wash, the
 * drag-path dots and the move forecast compose against this; nothing that commits does.
 *
 * `planningStateFor({ state, combatant, actorId, isGm })`: {@link previewIfControlled}'s arguments
 * plus the viewer's GM flag (`session.role === "gm"`, the `isGm` `CombatApp` and `CombatStage`
 * already hold). An args object, like {@link withLeadingActivation} and `boostRingCells`, because
 * four positional parameters trip the repo's `max-params` lint.
 * {@link previewIfControlled} first; when that state still fails `mayPlayMove` for `combatant`,
 * the unit is not the acting unit, and the viewer controls it by the GM-inclusive `canControl`
 * rule (`isGm || controlsCombatant`), the result is `planningTurnState` (a fresh turn where the
 * unit stands) with `planning: true`. Otherwise `planning: false` and the preview itself.
 *
 * **Why `canControl` here and `controlsActivation` in the preview.** The preview predicts a fold the
 * room will perform on a real commit, so it keys on strict slot identity plus the GM. A planning state commits
 * nothing, so the GM may plan any unit off-turn, which matters most for NPCs during a player's turn.
 *
 * **Commit paths keep {@link previewIfControlled} alone.** `canPlayMove` (`CombatApp.tsx`) and
 * `RemoteEncounterController.dispatchMove` never read this: a planning state is legal against a
 * turn that does not exist (`planningTurnState`'s never-commit invariant), and a planning drag that
 * dispatches `move_token` is the bug.
 */
export function planningStateFor(args: {
  state: EncounterState;
  combatant: Pick<Combatant, "id" | "controllerSlotId" | "coControllerSlotIds"> | undefined;
  /** The viewer's own slot id. */
  actorId: string | null | undefined;
  /** The viewer is the GM (`session.role === "gm"`). */
  isGm: boolean;
}): { state: EncounterState; planning: boolean } {
  const { state, combatant, actorId, isGm } = args;
  const preview = previewIfControlled(state, combatant, { actorId, isGm });
  const c = combatant === undefined ? undefined : preview.combatants[combatant.id];
  if (c === undefined || mayPlayMove(preview, c)) return { state: preview, planning: false };
  const turn = preview.activation.turn;
  if (turn.phase === "acting" && turn.actor === c.id) return { state: preview, planning: false };
  if (!isGm && !controlsCombatant(c, actorId)) return { state: preview, planning: false };
  const planned = planningTurnState(preview, c.id);
  return planned === preview ? { state: preview, planning: false } : { state: planned, planning: true };
}

/**
 * **Would the room fold an `activate_unit` in front of this caller's next action on this unit?**
 * The boolean half of {@link previewIfControlled}, derived from it by identity rather than by a
 * second copy of its condition — so the predicate and the state it predicts can never disagree.
 *
 * `previewActivation` returns the SAME object when it declines to preview, so `!==` is exactly
 * "the fold's first arm fired". Do not re-derive this as `perUnitState(...) === "ready" &&
 * controlsActivation(...)`: that is the copy this exists to prevent.
 */
export function wouldFoldActivation(
  state: EncounterState,
  combatant: Pick<Combatant, "id" | "controllerSlotId" | "coControllerSlotIds"> | undefined,
  viewer: FoldViewer,
): boolean {
  return previewIfControlled(state, combatant, viewer) !== state;
}

/**
 * **The D6 gesture builder** — `command` on its own, or `activate_unit` followed by `command`, as
 * **two ordinary commands** (`projects/pre-activation-affordances.md` 3.1).
 *
 * ## Why a client-side lead at all, when D1 says the fold is server-side
 *
 * Every other pre-activation gesture reaches the room as an `encounter_intent`, and
 * `RemoteEncounterController.sendIntent` runs no local dry-run — so the room sees it and folds
 * (D1/D2). The two **command**-channel members (`boost`, `prepare`) never get that far:
 * `RemoteController.dispatch`'s documented contract is *"a local reducer rejection is surfaced
 * immediately and NOT sent"*, and the domain reducer refuses both outright off-turn
 * (`handlers/budget.ts`, `handlers/attacks.ts`). An ungated Boost row without this lead is a
 * button that does nothing at all, silently — the exact failure mode D7 exists to prevent. So the
 * client emits the activation the room would have folded, and the room applies it through the same
 * `applyHookedCommand` path any explicit ACTIVATE TURN press takes.
 *
 * ## Gotcha — do NOT "tidy" these into a `batch`
 *
 * The room's `handle` routes by leaf `command.type`; `batch` is not a member of
 * `HOOKED_COMMAND_TYPE_RECORD` (`packages/rules/src/encounter/referee/dispatchClassification.ts`),
 * so a batch falls through to plain `applyCommand` and **silently skips both hook chains** —
 * `activate_unit`'s start-of-turn chain (the meltdown countdown tick, the auto-reveal guard) and
 * `boost`'s own post-Boost reveal hook. It compiles, it ships green, and it misbehaves at the
 * table. Two sequential commands preserve both chains. The next person to see two sequential
 * commands will want to collapse them; this paragraph is for them, and both
 * `preActivationBoostGesture.test.ts` and `backend/src/preActivationFold.integration.test.ts` go
 * red the moment they do.
 *
 * The lead carries `foldLead: true`, so the room merges the pair's two undo entries into one: one
 * undo reverts the action and the activation together, exactly like an intent's server-side fold.
 * That merge happens on the undo stack after both commands have walked their own hook chains, so
 * it is not the batch this gotcha forbids.
 *
 * Hand the result to {@link RemoteEncounterController.dispatchGesture}, never to `dispatch` in a
 * loop — the gesture window is what lets a lost race report its real cause (3.2).
 */
export function withLeadingActivation(args: {
  state: EncounterState;
  combatant: Pick<Combatant, "id" | "controllerSlotId" | "coControllerSlotIds"> | undefined;
  /** The viewer's own slot id — the same identity the room attributes a command to. */
  actorId: string;
  /** The viewer is the GM, who folds on every unit ({@link FoldViewer}). */
  isGm: boolean;
  /** The command-channel action being taken — `boost` and `prepare`, both of which have a browser row today (the mech/NPC QUICK columns). */
  command: EncounterCommand;
}): EncounterCommand[] {
  const { state, combatant, actorId, isGm, command } = args;
  if (combatant === undefined || !wouldFoldActivation(state, combatant, { actorId, isGm })) return [command];
  return [{ type: "activate_unit", actorId, combatantId: combatant.id, foldLead: true }, command];
}
